/**
 * Spezza le note del lead in campi etichettati.
 *
 * Le iscrizioni corsi e i form Meta/sito arrivano come un blocco di testo
 * (o come `raw`): in scheda devono comparire riga per riga, non come un
 * paragrafo unico.
 *
 * Quando i dati vengono da `raw`, le note scritte a mano dopo (con Modifica)
 * finiscono in `leftover`, cosi' non spariscono dietro ai campi del modulo.
 */

import { riepilogoIscrizione, type Iscrizione } from '@corsi/modulo.ts';

export interface LeadNoteField {
    label: string;
    value: string;
}

export interface LeadNoteSection {
    title: string;
    fields: LeadNoteField[];
}

/** Da dove vengono i dati: decide il titolo della scheda. */
export type LeadNotesKind = 'iscrizione' | 'modulo' | 'note';

export interface LeadNotesModel {
    kind: LeadNotesKind;
    headline: string | null;
    sections: LeadNoteSection[];
    leftover: string | null;
}

const CONTACT_KEYS = new Set([
    'email', 'mail', 'e-mail', 'phone', 'telefono', 'tel', 'cellulare', 'phone_number',
    'full_name', 'name', 'nome', 'first_name', 'last_name', 'company_name', 'company', 'azienda',
    'city', 'citta', 'città',
    'key', 'source', 'campaign_name', 'form_name', 'campagna', 'form',
]);

/** Campi che i moduli web aggiungono da soli: non li ha scritti la persona. */
const TECHNICAL_FORM_KEYS = new Set([
    'key', 'source', 'campaign_name', 'campagna', 'form_name', 'form',
    'g-recaptcha-response', 'h-captcha-response', 'cf-turnstile-response',
]);

/** Nomi dei campi del modulo del sito tradotti in etichette leggibili. */
const SITE_FORM_LABELS: Record<string, string> = {
    full_name: 'Nome e cognome', nome_cognome: 'Nome e cognome', nomecognome: 'Nome e cognome',
    name: 'Nome', nome: 'Nome', first_name: 'Nome', firstname: 'Nome',
    last_name: 'Cognome', lastname: 'Cognome', cognome: 'Cognome',
    email: 'Email', mail: 'Email', 'e-mail': 'Email',
    phone: 'Telefono', telefono: 'Telefono', tel: 'Telefono', phone_number: 'Telefono', cellulare: 'Cellulare',
    company_name: 'Azienda', company: 'Azienda', azienda: 'Azienda',
    city: 'Città', citta: 'Città', 'città': 'Città',
    subject: 'Oggetto', oggetto: 'Oggetto',
    message: 'Messaggio', messaggio: 'Messaggio', richiesta: 'Richiesta', note: 'Note', notes: 'Note',
    privacy: 'Privacy',
};

/** Gli stessi campi, nello stesso ordine, da cui site-lead-webhook prende le note del lead. */
const SITE_FORM_MESSAGE_KEYS = ['message', 'messaggio', 'note', 'notes', 'richiesta'];

const FIELD_SEP = /\s+[·•]\s+/;

function asText(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    return null;
}

function asObject(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

/** Come asText, ma accetta anche le caselle multiple (liste) e la spunta "on" dei moduli web. */
function formText(value: unknown): string | null {
    if (Array.isArray(value)) {
        const items = value.map(asText).filter((item): item is string => !!item);
        return items.length ? items.join(', ') : null;
    }
    const text = asText(value);
    return text?.toLowerCase() === 'on' ? 'Sì' : text;
}

/**
 * Le note scritte a mano oltre al testo che il lead aveva quando e' arrivato.
 * Se qualcuno ha aggiunto righe in fondo torna solo quelle; se ha riscritto
 * tutto, tutto il testo.
 */
export function addedNotes(notes: string | null | undefined, original: string | null): string | null {
    const text = notes?.trim();
    if (!text) return null;
    const start = original?.trim();
    if (!start) return text;
    if (text === start) return null;
    if (text.startsWith(start)) return text.slice(start.length).trim() || null;
    return text;
}

function isoToIt(iso: string): string {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

function siNo(value: unknown): string | null {
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    return null;
}

function section(
    title: string,
    pairs: Array<[string, string | null | undefined]>
): LeadNoteSection | null {
    const fields = pairs
        .map(([label, value]) => (value?.trim() ? { label, value: value.trim() } : null))
        .filter((field): field is LeadNoteField => !!field);
    return fields.length ? { title, fields } : null;
}

function originalIscrizioneNotes(i: Record<string, unknown>): string | null {
    try {
        return riepilogoIscrizione(i as unknown as Iscrizione);
    } catch {
        return null;
    }
}

function fromIscrizione(raw: Record<string, unknown>, notes: string | null | undefined): LeadNotesModel | null {
    const i = asObject(raw.iscrizione);
    if (!i) return null;

    const localita = [
        [asText(i.cap), asText(i.citta)].filter(Boolean).join(' '),
        asText(i.provinciaSigla) ? `(${asText(i.provinciaSigla)})` : null,
    ].filter(Boolean).join(' ');
    const indirizzo = [asText(i.indirizzo), localita, asText(i.regione), asText(i.nazione)]
        .filter(Boolean)
        .join(', ');

    const nascitaParti = [
        asText(i.luogoNascita),
        asText(i.dataNascita) ? isoToIt(String(i.dataNascita)) : null,
    ].filter(Boolean);
    const nascita = nascitaParti.length ? nascitaParti.join(', ') : null;

    let cfp: string | null = null;
    if (i.cfp === true) {
        cfp = [
            asText(i.ordine),
            asText(i.provinciaOrdine) ? `di ${asText(i.provinciaOrdine)}` : null,
            asText(i.numeroIscrizione) ? `iscrizione n. ${asText(i.numeroIscrizione)}` : null,
        ].filter(Boolean).join(', ') || 'Sì';
    } else if (i.cfp === false) {
        cfp = 'No';
    }

    const sections = [
        section('Corso', [['Corso', asText(i.corso)]]),
        section('Anagrafica', [
            ['Nato/a', nascita],
            ['Codice fiscale', asText(i.codiceFiscale)],
            ['Titolo', asText(i.titolo)],
        ]),
        section('Lavoro', [
            ['Qualifica', asText(i.qualifica)],
            ['Azienda / Ente', asText(i.azienda)],
            ['Attività', asText(i.attivita)],
            ['Dipendenti', asText(i.dipendenti)],
        ]),
        section('Indirizzo', [['Indirizzo', indirizzo || null]]),
        section('Formazione', [
            ['Crediti CFP', cfp],
            ['Ore aggiornamento 81/08', siNo(i.ore81)],
        ]),
        section('Consensi', [
            ['Comunicazioni SLAB', siNo(i.consensoInformazioni)],
            ['Dati alle aziende partecipanti', siNo(i.consensoPartner)],
            ['Profilazione', siNo(i.consensoProfilazione)],
        ]),
    ].filter((item): item is LeadNoteSection => !!item);

    if (!sections.length) return null;
    return {
        kind: 'iscrizione',
        headline: 'Iscrizione dal modulo corsi',
        sections,
        leftover: addedNotes(notes, originalIscrizioneNotes(i)),
    };
}

function humanizeKey(key: string): string {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\w/, (char) => char.toUpperCase());
}

/** Moduli Meta e Google: nome, email e telefono stanno già nei Dettagli. Le note le scrive solo chi lavora il lead. */
function fromKeyValueMap(map: Record<string, unknown>, title: string, notes: string | null | undefined): LeadNotesModel | null {
    const fields: LeadNoteField[] = [];
    for (const [key, value] of Object.entries(map)) {
        if (CONTACT_KEYS.has(key.toLowerCase())) continue;
        const text = asText(value);
        if (!text) continue;
        fields.push({ label: humanizeKey(key), value: text });
    }
    const leftover = addedNotes(notes, null);
    if (!fields.length && !leftover) return null;
    return { kind: 'modulo', headline: null, sections: fields.length ? [{ title, fields }] : [], leftover };
}

/**
 * Modulo contatti del sito: si vede tutto quello che la persona ha compilato,
 * nell'ordine del modulo, contatti compresi. Si saltano solo i campi tecnici.
 */
function fromSiteForm(form: Record<string, unknown>, notes: string | null | undefined): LeadNotesModel | null {
    const fields: LeadNoteField[] = [];
    for (const [key, value] of Object.entries(form)) {
        const lower = key.toLowerCase();
        if (lower.startsWith('_') || TECHNICAL_FORM_KEYS.has(lower)) continue;
        const text = formText(value);
        if (!text) continue;
        fields.push({ label: SITE_FORM_LABELS[lower] ?? humanizeKey(key), value: text });
    }

    const messageKey = SITE_FORM_MESSAGE_KEYS.find((key) => asText(form[key]));
    const leftover = addedNotes(notes, messageKey ? asText(form[messageKey]) : null);
    if (!fields.length && !leftover) return null;
    return { kind: 'modulo', headline: null, sections: fields.length ? [{ title: 'Dati form', fields }] : [], leftover };
}

function fromRawMaps(raw: Record<string, unknown>, notes: string | null | undefined): LeadNotesModel | null {
    const fieldData = asObject(raw.field_data_raw);
    if (fieldData) return fromKeyValueMap(fieldData, 'Dati form', notes);
    const columns = asObject(raw.columns_raw);
    if (columns) return fromKeyValueMap(columns, 'Dati form', notes);
    const form = asObject(raw.form);
    if (form) return fromSiteForm(form, notes);
    return null;
}

function parseLabeled(segment: string): LeadNoteField | null {
    const match = segment.match(/^(.{2,48}?):\s+(.+)$/);
    if (!match) return null;
    const label = match[1].trim();
    const value = match[2].trim();
    if (!label || !value) return null;
    if (/https?$/i.test(label)) return null;
    return { label, value };
}

/** Interpreta il riepilogo testuale (e note libere) in campi etichettati. */
export function parseLeadNotesText(notes: string): LeadNotesModel {
    const lines = notes.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    let headline: string | null = null;
    const fields: LeadNoteField[] = [];
    const leftoverLines: string[] = [];

    for (const line of lines) {
        const corsoMatch = line.match(/^Iscrizione dal modulo corsi(?:\s+[—-]\s+Corso:\s+(.+))?$/i);
        if (corsoMatch) {
            headline = 'Iscrizione dal modulo corsi';
            if (corsoMatch[1]) fields.push({ label: 'Corso', value: corsoMatch[1] });
            continue;
        }

        const consensiMatch = line.match(/^Consensi:\s+(.+)$/i);
        if (consensiMatch) {
            const bits = consensiMatch[1].split(FIELD_SEP);
            let added = false;
            for (const bit of bits) {
                const flag = bit.match(/^(.*?)[\s:]+(sì|si|no)$/i);
                if (flag) {
                    const label = flag[1].replace(/:$/, '').trim().replace(/^\w/, (char) => char.toUpperCase());
                    const value = /^no$/i.test(flag[2]) ? 'No' : 'Sì';
                    if (label) {
                        fields.push({ label, value });
                        added = true;
                    }
                }
            }
            if (added) continue;
            fields.push({ label: 'Consensi', value: consensiMatch[1] });
            continue;
        }

        const natoMatch = line.match(
            /^Nato\/a a (.+?) il (\d{1,2}\/\d{1,2}\/\d{4})(?:\s*[·•\-]\s*CF\s+(.+))?$/i
        );
        if (natoMatch) {
            fields.push({ label: 'Nato/a', value: `${natoMatch[1]}, ${natoMatch[2]}` });
            if (natoMatch[3]) fields.push({ label: 'Codice fiscale', value: natoMatch[3] });
            continue;
        }

        const parts = line.split(FIELD_SEP);
        const labeled = parts.map(parseLabeled);
        const labeledCount = labeled.filter(Boolean).length;
        if (labeledCount > 0 && labeledCount === parts.length) {
            fields.push(...(labeled as LeadNoteField[]));
            continue;
        }
        const whole = parseLabeled(line);
        if (whole) {
            fields.push(whole);
            continue;
        }

        leftoverLines.push(line);
    }

    const leftover = leftoverLines.join('\n').trim() || null;
    return { kind: headline ? 'iscrizione' : 'note', headline, sections: groupNoteFields(fields), leftover };
}

const NOTE_GROUPS: Array<{ title: string; labels: string[] }> = [
    { title: 'Corso', labels: ['Corso'] },
    { title: 'Anagrafica', labels: ['Nato/a', 'Codice fiscale', 'Titolo'] },
    { title: 'Lavoro', labels: ['Qualifica', 'Azienda/Ente', 'Azienda / Ente', 'Attività', 'Dipendenti'] },
    { title: 'Indirizzo', labels: ['Indirizzo'] },
    { title: 'Formazione', labels: ['Crediti CFP', 'Ore aggiornamento 81/08'] },
    { title: 'Consensi', labels: ['Comunicazioni SLAB', 'Dati alle aziende partecipanti', 'Profilazione', 'Consensi'] },
];

function groupNoteFields(fields: LeadNoteField[]): LeadNoteSection[] {
    if (!fields.length) return [];
    const used = new Set<number>();
    const sections: LeadNoteSection[] = [];
    for (const group of NOTE_GROUPS) {
        const matched: LeadNoteField[] = [];
        fields.forEach((field, index) => {
            if (used.has(index) || !group.labels.includes(field.label)) return;
            used.add(index);
            matched.push(field);
        });
        if (matched.length) sections.push({ title: group.title, fields: matched });
    }
    const rest = fields.filter((_, index) => !used.has(index));
    if (rest.length) sections.push({ title: sections.length ? 'Altro' : 'Dettagli', fields: rest });
    return sections;
}

export function leadNotesModel(input: {
    notes?: string | null;
    raw?: Record<string, unknown> | null;
}): LeadNotesModel | null {
    const raw = input.raw ?? null;
    if (raw) {
        const structured = fromIscrizione(raw, input.notes);
        if (structured) return structured;
        const mapped = fromRawMaps(raw, input.notes);
        if (mapped) return mapped;
    }

    const notes = input.notes?.trim();
    if (!notes) return null;
    return parseLeadNotesText(notes);
}

export function hasLeadNotes(model: LeadNotesModel | null): boolean {
    if (!model) return false;
    return Boolean(model.headline || model.leftover || model.sections.some((s) => s.fields.length));
}
